import { ChannelModel, TradingModel } from '@aionzdev/modules';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface FindAllCanaisParams {
  take: number;
  currentPage: number;
  isOfficial?: boolean;
}

export interface FindAllOfertasParams {
  status: string[];
  order: string;
  page: number;
  pageSize: number;
  /** Dono do canal — usado quando o canal não tem idEmpresa vinculado. */
  idUsuario?: string | number;
  /** Empresa do canal — tem precedência sobre idUsuario (mesma regra do ebarn-app em CanalService.getOffersChannel). */
  idEmpresa?: number;
  /** Termo de busca textual — filtrado no backend (TradingViewQueryFilters). */
  search?: string;
}

/** Formato real devolvido por GET /canal/all — usa `_id` (Mongo), não `id`. */
interface CanalResponse {
  _id: string;
  nome: string;
  titulo: string;
  bio: string;
  slug: string;
  site?: string;
  tags?: string[];
  banner?: string;
  bannerFundo?: string;
  isOfficial?: boolean;
  usuarioId?: string | number;
  seguidores?: number | unknown[];
  totalMembers?: number;
}

/** Subconjunto de GET /canal/by-slug/:slug usado pelo Marketplace B2B. */
export interface CanalDetalheResponse {
  _id: string;
  slug: string;
  usuarioId?: string | number;
  idEmpresa?: number;
}

@Injectable({ providedIn: 'root' })
export class CatalogosApiService {
  private readonly _http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}`;

  findAllCanais(params: FindAllCanaisParams): Observable<ChannelModel[]> {
    const httpParams = new HttpParams({
      fromObject: {
        take: params.take,
        currentPage: params.currentPage,
        hasOfertas: true,
        ...(params.isOfficial !== undefined ? { isOfficial: params.isOfficial } : {})
      }
    });

    return this._http
      .get<CanalResponse[]>(`${this.baseUrl}/canal/all`, { params: httpParams })
      .pipe(map(canais => canais.map(canal => this.mapCanalToChannelModel(canal))));
  }

  findCanalBySlug(slug: string): Observable<CanalDetalheResponse> {
    return this._http.get<CanalDetalheResponse>(`${this.baseUrl}/canal/by-slug/${encodeURIComponent(slug)}`);
  }

  private mapCanalToChannelModel(canal: CanalResponse): ChannelModel {
    return {
      id: canal._id,
      name: canal.nome,
      title: canal.titulo,
      bio: canal.bio,
      slug: canal.slug,
      site: canal.site,
      tags: canal.tags,
      banner: canal.banner,
      bannerFundo: canal.bannerFundo,
      isOfficial: canal.isOfficial,
      totalFollowers: Array.isArray(canal.seguidores) ? canal.seguidores.length : (canal.seguidores ?? 0),
      totalMembers: canal.totalMembers,
      tenantId: '',
      userId: canal.usuarioId?.toString() ?? ''
    };
  }

  findAllOfertas(params: FindAllOfertasParams): Observable<TradingModel[]> {
    const httpParams = this.buildOfertasParams(params);

    return this._http.get<TradingModel[]>(`${this.baseUrl}/pg/negociacao-compra-venda/all`, {
      params: httpParams
    });
  }

  /**
   * Igual findAllOfertas, mas também devolve o total de registros (para
   * mat-paginator) — a API expõe isso via header X-Total-Count, não no
   * corpo, então precisa de observe: 'response'.
   */
  findAllOfertasPaginado(params: FindAllOfertasParams): Observable<{ items: TradingModel[]; total: number }> {
    const httpParams = this.buildOfertasParams(params);

    return this._http
      .get<TradingModel[]>(`${this.baseUrl}/pg/negociacao-compra-venda/all`, {
        params: httpParams,
        observe: 'response'
      })
      .pipe(
        map(response => ({
          items: response.body ?? [],
          total: Number(response.headers.get('X-Total-Count') ?? response.body?.length ?? 0)
        }))
      );
  }

  private buildOfertasParams(params: FindAllOfertasParams): HttpParams {
    const search = params.search?.trim();

    return new HttpParams({
      fromObject: {
        status: params.status.join(','),
        order: params.order,
        page: params.page,
        pageSize: params.pageSize,
        ...(params.idEmpresa ? { idEmpresa: params.idEmpresa } : params.idUsuario ? { idUsuario: params.idUsuario } : {}),
        ...(search ? { search } : {})
      }
    });
  }

  findOfertaById(id: string | number): Observable<TradingModel> {
    const httpParams = new HttpParams({ fromObject: { id } });

    return this._http.get<TradingModel>(`${this.baseUrl}/pg/negociacao-compra-venda/find-negociacao-by-id`, {
      params: httpParams
    });
  }

  registerView(interactionId: number): Observable<void> {
    return this._http.post<void>(`${this.baseUrl}/interactions/view`, { interactionId });
  }

  registerClick(interactionId: number): Observable<void> {
    return this._http.post<void>(`${this.baseUrl}/interactions/click`, { interactionId });
  }
}
